import {attemptMemberIds, versionFor, type LibraryState, type StudentAttempt} from "./activity-library";
import {isEmptyAbsentCopy} from "./attendance-results";
import {attemptSessionId, latestWork} from "./activity-continuity";

/** Read-only projection. Historical copies stay intact, including pre-continuation imports. */
export function currentCorrectionCopies(state: LibraryState, includeEmptyAbsences = false): StudentAttempt[] {
  const superseded = new Set(state.attempts.flatMap(a => a.continuedFromAttemptId ? [a.continuedFromAttemptId] : []));
  const sessionDate = (a: StudentAttempt) => state.digitalSessions?.find(d => d.id === attemptSessionId(a))?.startedAt
    ?? state.assignments.find(row => row.id === a.assignmentId)?.assignedAt ?? a.startedAt ?? "";
  const ordered = state.attempts.filter(a => !superseded.has(a.id)).sort((a, b) =>
    sessionDate(b).localeCompare(sessionDate(a)) ||
    Number(b.ownerType === "group") - Number(a.ownerType === "group") ||
    (latestWork(state, [a, b])?.id === a.id ? -1 : 1));
  const represented = new Set<string>();
  const result: StudentAttempt[] = [];
  for (const attempt of ordered) {
    const keys = attemptMemberIds(attempt).map(id => JSON.stringify([attempt.classId, attempt.activityId, id]));
    // A shared copy is indivisible: never show it a second time through one of its members.
    if (!keys.length || keys.some(key => represented.has(key))) continue;
    keys.forEach(key => represented.add(key));
    result.push(attempt);
  }
  // Select current ownership before filtering status; an unfinished newer copy must
  // not make an archived submitted copy reappear in Corrections.
  return result.filter(a => ["not_started", "in_progress", "submitted", "reviewed", "completed"].includes(a.status)
    && (["not_started", "in_progress"].includes(a.status) || includeEmptyAbsences || a.status === "completed" || !isEmptyAbsentCopy(a, versionFor(state, a))));
}
